import { useState } from 'react';
import {
  applyToAllDays,
  copyDayTo,
  createDefaultHours,
  setDayClosed,
  setDayTime,
  summarizeHours,
  validateHours,
  DAY_LABELS,
  DAY_ORDER,
  DAY_SHORT,
  OperatingHours,
  DayKey,
  TIME_OPTIONS,
} from '../../features/addresses/operatingHours';
import './OperatingHoursEditor.scss';

export interface OperatingHoursEditorProps {
  value: OperatingHours | null;
  onChange: (next: OperatingHours | null) => void;
  disabled?: boolean;
  // Master-toggle label and sub-hint. Default to the delivery-location wording;
  // callers (e.g. warehouses) override to "Set warehouse hours".
  label?: string;
  hint?: string;
}

function TimeSelect({
  value,
  onChange,
  disabled,
  ariaLabel,
}: {
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaLabel: string;
}) {
  return (
    <select
      className="operating-hours__time"
      aria-label={ariaLabel}
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="" disabled>
        —
      </option>
      {TIME_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// Per-day receiving-hours editor: master on/off, apply-to-all quick-fill, and a
// per-day "Copy to…". Controlled; `null` means no hours set.
export default function OperatingHoursEditor({
  value,
  onChange,
  disabled = false,
  label = 'Set receiving hours',
  hint = 'Shown to carriers on the Bill of Lading and the load email.',
}: OperatingHoursEditorProps) {
  const [fillOpen, setFillOpen] = useState('08:00');
  const [fillClose, setFillClose] = useState('17:00');
  const [copyFrom, setCopyFrom] = useState<DayKey | null>(null);
  const [copyTargets, setCopyTargets] = useState<Set<DayKey>>(new Set());

  const enabled = value !== null;
  const validation = validateHours(value);
  const preview = summarizeHours(value);

  const toggleEnabled = (on: boolean) => {
    onChange(on ? createDefaultHours() : null);
    setCopyFrom(null);
  };

  const openCopy = (day: DayKey) => {
    setCopyFrom(day);
    setCopyTargets(new Set(DAY_ORDER.filter((d) => d !== day)));
  };

  const applyCopy = () => {
    if (value && copyFrom) {
      onChange(copyDayTo(value, copyFrom, Array.from(copyTargets)));
    }
    setCopyFrom(null);
  };

  return (
    <div className="operating-hours">
      <label className="operating-hours__master">
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={(e) => toggleEnabled(e.target.checked)}
        />
        <span>{label}</span>
      </label>
      <div className="muted tiny operating-hours__hint">{hint}</div>

      {enabled && value && (
        <>
          <div className="operating-hours__quickfill">
            <span className="operating-hours__quickfill-label">Apply to all days:</span>
            <TimeSelect
              value={fillOpen}
              onChange={setFillOpen}
              disabled={disabled}
              ariaLabel="Apply to all days, open time"
            />
            <span className="operating-hours__dash">–</span>
            <TimeSelect
              value={fillClose}
              onChange={setFillClose}
              disabled={disabled}
              ariaLabel="Apply to all days, close time"
            />
            <button
              type="button"
              className="btn btn-sm operating-hours__apply-all"
              disabled={disabled || fillOpen >= fillClose}
              onClick={() => onChange(applyToAllDays(value, { open: fillOpen, close: fillClose }))}
            >
              Apply to all
            </button>
          </div>

          <div className="operating-hours__rows">
            {DAY_ORDER.map((day) => {
              const d = value[day];
              const error = validation.errors[day];
              return (
                <div key={day} className="operating-hours__row">
                  <div className="operating-hours__day">{DAY_LABELS[day]}</div>

                  <label className="operating-hours__opentoggle">
                    <input
                      type="checkbox"
                      aria-label={`${DAY_LABELS[day]} open`}
                      checked={!d.closed}
                      disabled={disabled}
                      onChange={(e) => onChange(setDayClosed(value, day, !e.target.checked))}
                    />
                    <span>Open</span>
                  </label>

                  {d.closed ? (
                    <div className="operating-hours__closed muted">Closed</div>
                  ) : (
                    <div className="operating-hours__times">
                      <TimeSelect
                        value={d.open}
                        onChange={(v) => onChange(setDayTime(value, day, 'open', v))}
                        disabled={disabled}
                        ariaLabel={`${DAY_LABELS[day]} open time`}
                      />
                      <span className="operating-hours__dash">–</span>
                      <TimeSelect
                        value={d.close}
                        onChange={(v) => onChange(setDayTime(value, day, 'close', v))}
                        disabled={disabled}
                        ariaLabel={`${DAY_LABELS[day]} close time`}
                      />
                    </div>
                  )}

                  <div className="operating-hours__copy">
                    <button
                      type="button"
                      className="operating-hours__copy-btn"
                      aria-label={`Copy ${DAY_LABELS[day]} hours`}
                      disabled={disabled}
                      onClick={() => (copyFrom === day ? setCopyFrom(null) : openCopy(day))}
                    >
                      ⧉ Copy to…
                    </button>
                    {copyFrom === day && (
                      <div className="operating-hours__popover" role="dialog">
                        <div className="operating-hours__popover-title">
                          Copy {DAY_LABELS[day]} to:
                        </div>
                        <div className="operating-hours__popover-days">
                          {DAY_ORDER.filter((d2) => d2 !== day).map((d2) => (
                            <label key={d2} className="operating-hours__popover-day">
                              <input
                                type="checkbox"
                                checked={copyTargets.has(d2)}
                                onChange={(e) => {
                                  const next = new Set(copyTargets);
                                  if (e.target.checked) next.add(d2);
                                  else next.delete(d2);
                                  setCopyTargets(next);
                                }}
                              />
                              <span>{DAY_SHORT[d2]}</span>
                            </label>
                          ))}
                        </div>
                        <div className="operating-hours__popover-actions">
                          <button
                            type="button"
                            className="operating-hours__link"
                            onClick={() =>
                              setCopyTargets(new Set(DAY_ORDER.filter((d2) => d2 !== day)))
                            }
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={copyTargets.size === 0}
                            onClick={applyCopy}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {error && <div className="operating-hours__error">{error}</div>}
                </div>
              );
            })}
          </div>

          {preview && (
            <div className="operating-hours__preview">
              <span className="operating-hours__preview-label">Drivers will see:</span> {preview}
            </div>
          )}
        </>
      )}
    </div>
  );
}
